'use client'

import { isEmpty } from 'es-toolkit/compat'
import { useEffect } from 'react'

import {
  usePlaceListQuery,
  formatSearchKeyword,
  RECOMMENDED_PLACE_IDS,
} from '@/entities/place'
import useSearchPlace from '@/features/seoPlaceSearch/model/useSearchPlace'
import { AddressSearchBottomSheet } from '@/features/seoPlaceSearch/ui/AddressSearchBottomSheet'
import { PlaceRecommendList } from '@/features/seoPlaceSearch/ui/PlaceRecommendList'
import { PlaceSearchBanner } from '@/features/seoPlaceSearch/ui/PlaceSearchBanner'
import { PlaceSearchField } from '@/features/seoPlaceSearch/ui/PlaceSearchField'
import { PlaceSearchList } from '@/features/seoPlaceSearch/ui/PlaceSearchList'
import {
  ListHeader,
  ListHeaderLowerText,
  ListHeaderTitleText,
  DividerHorizontal,
  ProgressIndicator,
} from '@/shared/design-system/components'
import { logEvent } from '@/shared/external/firebase/analytics'
import { DEFAULT_CURRENT_LOCATION, formatAddress } from '@/shared/lib'
import { useOutsideTouch } from '@/shared/model/useOutsideTouch'

const PlaceSearchContent = () => {
  useEffect(() => {
    logEvent('current_view', {
      label: 'WebSearchView',
    })
  }, [])

  const {
    state: {
      inputRef,
      keyword,
      inputValue,
      isFetchingNextPage,
      hasNextPage,
      isError,
      isLoading,
      isAddressBottomSheetOpen,
    },
    data: { placeListData, latitude, longitude, addressName },
    actions: {
      fetchNextPage,
      handleSearchPlace,
      handleInputChange,
      handleClearInput,
      handleOpenAddressBottomSheet,
      handleCloseAddressBottomSheet,
      handleRetrySearchPlace,
    },
  } = useSearchPlace()

  const {
    data: recommendedPlaceListData,
    isError: recommendedPlaceListError,
    isLoading: isLoadingRecommendedPlaceList,
    refetch: handleRetryPlaceList,
  } = usePlaceListQuery({
    id:
      process.env.NODE_ENV === 'development'
        ? RECOMMENDED_PLACE_IDS.DEV
        : RECOMMENDED_PLACE_IDS.PROD,
  })

  const ref = useOutsideTouch(() => {
    if (inputRef.current && document.activeElement === inputRef.current) {
      inputRef.current.blur()
    }
  })

  // 기본 위치인지 확인
  const isDefaultLocation =
    latitude === DEFAULT_CURRENT_LOCATION.LATITUDE &&
    longitude === DEFAULT_CURRENT_LOCATION.LONGITUDE

  const hasResults =
    placeListData?.totalElements && placeListData?.totalElements > 0
  const resultCount = placeListData?.totalElements?.toLocaleString() ?? 0
  const formattedAddressName = formatAddress(addressName ?? '')
  const region3DepthName =
    formattedAddressName?.split(' ')[
      formattedAddressName?.split(' ').length - 1
    ] ?? '위치선택'

  const titleConfig = {
    line1: isEmpty(keyword)
      ? '제일 인기 있는'
      : isDefaultLocation
        ? hasResults
          ? `제일 인기 있는 ${formatSearchKeyword(keyword)}`
          : `검색하신 ${formatSearchKeyword(keyword)}`
        : `${formatAddress(addressName ?? '')} ${formatSearchKeyword(keyword)}`,
    line2:
      hasResults || isEmpty(keyword) ? '정비소를 모았어요' : '정비소가 없어요',
  }

  return (
    <>
      {(isLoading || isLoadingRecommendedPlaceList) && (
        <ProgressIndicator
          isLoading={isLoading || isLoadingRecommendedPlaceList}
        />
      )}

      {!isLoading && !isLoadingRecommendedPlaceList && (
        <div className='flex flex-col max-w-[768px] mx-auto min-h-screen'>
          <PlaceSearchField
            inputRef={inputRef}
            inputValue={inputValue}
            region3DepthName={
              !isDefaultLocation ? region3DepthName : '위치선택'
            }
            onClickSearchPlace={handleSearchPlace}
            onChangeInput={handleInputChange}
            onClickClearInput={handleClearInput}
            onClickSearchAddress={handleOpenAddressBottomSheet}
          />

          {formattedAddressName && (
            <div>
              <ListHeader
                title={
                  <ListHeaderTitleText
                    title={
                      <>
                        <p>{titleConfig.line1}</p>
                        <p>{titleConfig.line2}</p>
                      </>
                    }
                    typography='text-heading-3-bold'
                    className='truncate'
                  />
                }
                lower={
                  <ListHeaderLowerText
                    text={
                      keyword
                        ? `검색 결과 ${resultCount}개`
                        : '원하는 정비소도 검색할 수 있어요'
                    }
                    typography='text-body-1-medium'
                  />
                }
              />
            </div>
          )}

          <DividerHorizontal
            h='h-stroke-width-x10'
            color='bg-stroke-neutral-normal'
          />

          <PlaceSearchBanner />

          {isEmpty(keyword) && (
            <div ref={ref}>
              <PlaceRecommendList
                data={recommendedPlaceListData}
                isError={recommendedPlaceListError}
                handleRetryPlaceList={handleRetryPlaceList}
              />
            </div>
          )}

          {keyword && (
            <PlaceSearchList
              data={placeListData}
              isFetchingNextPage={isFetchingNextPage}
              isError={isError}
              hasNextPage={hasNextPage}
              fetchNextPage={fetchNextPage}
              handleRetrySearchPlace={handleRetrySearchPlace}
            />
          )}
        </div>
      )}

      {/* 바텀 시트 영역 */}
      {isAddressBottomSheetOpen && (
        <AddressSearchBottomSheet
          isOpen={isAddressBottomSheetOpen}
          onClose={handleCloseAddressBottomSheet}
        />
      )}
    </>
  )
}

export default PlaceSearchContent
